import { CrudForm } from '../shared/CrudPage';
import { enumEntries, payrollService } from '@kss/api';
import type { Payroll, PayrollCreate, PayrollFilter } from '@kss/api';

/**
 * Payroll form — wired to the live schema verified 2026-05-05.
 *
 * The live API models a Payroll as a **company-monthly batch run**
 * (one row per `(month, year)`), NOT a per-employee record. The
 * per-employee detail lives in the `payslips[]` collection. The
 * legacy form posted `employeeId` / `period` / `basicSalary` / etc.
 * — strict-mode binding rejects every one of those keys with
 * `UNKNOWN_FIELD`.
 *
 * Live schema: `{ payrollNumber, month, year, processedDate, status,
 * totalGross, totalDeductions, totalNet }`. Per-employee payslips
 * will land once the backend documents whether `payslips[]` is a
 * nested-create field or a separate `/Payslip` controller (Item
 * 5d in the backend brief).
 */

/* Single source of truth — see enums.ts PayrollStatusLabels. */
const STATUS_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('PayrollStatus').map((e) => [e.label, e.value]),
);
const LABEL_BY_STATUS: Record<number, string> = Object.fromEntries(
  Object.entries(STATUS_BY_LABEL).map(([k, v]) => [v, k]),
);

type F = {
  payrollNumber: string;
  month: number;
  year: number;
  processedDate: string;
  status: string;
  totalGross: number;
  totalDeductions: number;
  totalNet: number;
};
const now = new Date();
const today = now.toISOString().slice(0, 10);
const empty: F = {
  payrollNumber: `PAY-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
  month: now.getMonth() + 1,
  year: now.getFullYear(),
  processedDate: today,
  status: 'Draft',
  totalGross: 0,
  totalDeductions: 0,
  totalNet: 0,
};

export default function NewPayroll() {
  return (
    <CrudForm<Payroll, PayrollCreate, PayrollFilter, F>
      title="payroll"
      service={payrollService}
      idKey="payrollId"
      basePath="/panel/payrolls"
      emptyForm={empty}
      fields={[
        { key: 'payrollNumber', label: 'Payroll number', required: true, placeholder: 'PAY-2026-04' },
        { key: 'month', label: 'Month (1–12)', type: 'number', required: true },
        { key: 'year', label: 'Year', type: 'number', required: true },
        { key: 'processedDate', label: 'Processed date', type: 'date' },
        {
          key: 'status',
          label: 'Status',
          type: 'select',
          options: Object.keys(STATUS_BY_LABEL).map((v) => ({ value: v, label: v })),
        },
        { key: 'totalGross', label: 'Total gross (₹)', type: 'number' },
        { key: 'totalDeductions', label: 'Total deductions (₹)', type: 'number' },
        { key: 'totalNet', label: 'Total net (₹)', type: 'number' },
      ]}
      fromEntity={(r) => ({
        payrollNumber: r.payrollNumber ?? '',
        month: Number(r.month ?? now.getMonth() + 1),
        year: Number(r.year ?? now.getFullYear()),
        processedDate: ((r.processedDate ?? r.processedAt) ?? `${today}T00:00:00`).slice(0, 10),
        status:
          typeof r.status === 'number'
            ? LABEL_BY_STATUS[r.status] ?? 'Draft'
            : String(r.status ?? 'Draft'),
        totalGross: Number(r.totalGross ?? 0),
        totalDeductions: Number(r.totalDeductions ?? 0),
        totalNet: Number(r.totalNet ?? 0),
      })}
      toEntity={(f, id) => ({
        payrollId: id,
        payrollNumber: f.payrollNumber,
        month: f.month,
        year: f.year,
        processedDate: f.processedDate ? `${f.processedDate}T00:00:00` : undefined,
        status: STATUS_BY_LABEL[f.status] ?? 1,
        totalGross: f.totalGross || undefined,
        totalDeductions: f.totalDeductions || undefined,
        totalNet: f.totalNet || undefined,
      })}
      validate={(f) =>
        !f.payrollNumber.trim()
          ? 'Payroll number is required'
          : f.month < 1 || f.month > 12
          ? 'Month must be between 1 and 12'
          : f.year < 2000
          ? 'Year is required'
          : null
      }
    />
  );
}
